import { Box, Button, TextField } from "@mui/material";
import React from "react";
import { Link } from "react-admin";
import { useTranslate } from "react-admin";
import { useForm } from "react-hook-form";

import { PASSWORD_MIN_LENGTH } from "../lib/validation";
import { space } from "../theme/designTokens";
import { AuthFormHeader } from "./AuthFormHeader";

export type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

type ResetPasswordFormProps = {
  loading: boolean;
  onSubmit: (data: ResetPasswordFormData) => void;
  onBackToLogin: () => void;
};

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ loading, onSubmit, onBackToLogin }) => {
  const translate = useTranslate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormData>({
    mode: "onChange",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");

  return (
    <>
      <AuthFormHeader title={translate("resetPassword.title")} description={translate("resetPassword.description")} />

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          label={translate("resetPassword.password")}
          type="password"
          margin="normal"
          autoComplete="new-password"
          autoFocus
          disabled={loading}
          required
          error={!!errors.password}
          helperText={errors.password?.message}
          {...register("password", {
            required: translate("resetPassword.passwordRequired"),
            minLength: {
              value: PASSWORD_MIN_LENGTH,
              message: translate("resetPassword.passwordMinLength", { minLength: PASSWORD_MIN_LENGTH }),
            },
          })}
        />

        <TextField
          fullWidth
          label={translate("resetPassword.confirmPassword")}
          type="password"
          margin="normal"
          autoComplete="new-password"
          disabled={loading}
          required
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          {...register("confirmPassword", {
            required: translate("resetPassword.confirmPasswordRequired"),
            validate: value => value === password || translate("resetPassword.passwordsDoNotMatch"),
          })}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading || !isValid}
          sx={{ marginTop: `${space.xl}px` }}
        >
          {loading ? translate("resetPassword.resetting") : translate("resetPassword.submit")}
        </Button>

        <Box sx={{ marginTop: `${space.xl}px` }}>
          <Link
            to="#"
            onClick={e => {
              e.preventDefault();
              onBackToLogin();
            }}
          >
            {translate("resetPassword.backToLogin")}
          </Link>
        </Box>
      </Box>
    </>
  );
};
